import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  useAttraction,
  useAttractionRelationships,
  useAssignPackageToAttraction,
  useRemovePackageFromAttraction,
  useAssignGroupTripToAttraction,
  useRemoveGroupTripFromAttraction,
  useAssignActivityToAttraction,
  useRemoveActivityFromAttraction,
  useAssignHotelToAttraction,
  useRemoveHotelFromAttraction,
} from '../../../lib/hooks/useAttractions';
import { usePackages } from '../../../lib/hooks/usePackages';
import { useGroupTrips } from '../../../lib/hooks/useGroupTrips';
import { useActivities } from '../../../lib/hooks/useActivities';
import { useHotels } from '../../../lib/hooks/useHotels';

const AttractionRelationshipsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const attractionId = parseInt(id || '0');
  const navigate = useNavigate();

  const [selectedPackageId, setSelectedPackageId] = useState<number | ''>('');
  const [selectedGroupTripId, setSelectedGroupTripId] = useState<number | ''>('');
  const [selectedActivityId, setSelectedActivityId] = useState<number | ''>('');
  const [selectedHotelId, setSelectedHotelId] = useState<number | ''>('');

  const { data: attraction, isLoading: isLoadingAttraction } = useAttraction(attractionId);
  const { data: relationships, isLoading: isLoadingRelationships, refetch } = useAttractionRelationships(attractionId);
  const { data: allPackages, isLoading: isLoadingPackages } = usePackages();
  const { data: allGroupTrips, isLoading: isLoadingGroupTrips } = useGroupTrips();
  const { data: allActivities, isLoading: isLoadingActivities } = useActivities();
  const { data: allHotels, isLoading: isLoadingHotels } = useHotels();

  const { mutate: assignPackage, isPending: isAssigningPackage } = useAssignPackageToAttraction();
  const { mutate: removePackage, isPending: isRemovingPackage } = useRemovePackageFromAttraction();
  const { mutate: assignGroupTrip, isPending: isAssigningGroupTrip } = useAssignGroupTripToAttraction();
  const { mutate: removeGroupTrip, isPending: isRemovingGroupTrip } = useRemoveGroupTripFromAttraction();
  const { mutate: assignActivity, isPending: isAssigningActivity } = useAssignActivityToAttraction();
  const { mutate: removeActivity, isPending: isRemovingActivity } = useRemoveActivityFromAttraction();
  const { mutate: assignHotel, isPending: isAssigningHotel } = useAssignHotelToAttraction();
  const { mutate: removeHotel, isPending: isRemovingHotel } = useRemoveHotelFromAttraction();

  if (isLoadingAttraction || isLoadingRelationships || isLoadingPackages || isLoadingGroupTrips) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="spinner"></div>
        <p className="ml-2">Loading data...</p>
      </div>
    );
  }

  if (!attraction || !relationships) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-md">
        Error loading data. Please try again or go back to the attractions list.
      </div>
    );
  }

  const handleAssignPackage = () => {
    if (selectedPackageId !== '') {
      assignPackage(
        { attractionId, packageId: Number(selectedPackageId) },
        {
          onSuccess: () => {
            refetch();
            setSelectedPackageId('');
          }
        }
      );
    }
  };

  const handleRemovePackage = (packageId: number) => {
    removePackage(
      { attractionId, packageId },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  const handleAssignGroupTrip = () => {
    if (selectedGroupTripId !== '') {
      assignGroupTrip(
        { attractionId, groupTripId: Number(selectedGroupTripId) },
        {
          onSuccess: () => {
            refetch();
            setSelectedGroupTripId('');
          }
        }
      );
    }
  };

  const handleRemoveGroupTrip = (groupTripId: number) => {
    removeGroupTrip(
      { attractionId, groupTripId },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  const handleAssignActivity = () => {
    if (selectedActivityId !== '') {
      assignActivity(
        { attractionId, activityId: Number(selectedActivityId) },
        {
          onSuccess: () => {
            refetch();
            setSelectedActivityId('');
          }
        }
      );
    }
  };

  const handleRemoveActivity = (activityId: number) => {
    removeActivity(
      { attractionId, activityId },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  const handleAssignHotel = () => {
    if (selectedHotelId !== '') {
      assignHotel(
        { attractionId, hotelId: Number(selectedHotelId) },
        {
          onSuccess: () => {
            refetch();
            setSelectedHotelId('');
          }
        }
      );
    }
  };

  const handleRemoveHotel = (hotelId: number) => {
    removeHotel(
      { attractionId, hotelId },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  // Filter out packages that are already assigned to the attraction
  const unassignedPackages = allPackages?.filter(
    pkg => !relationships.package_ids?.includes(pkg.id)
  );

  // Filter out group trips that are already assigned to the attraction
  const unassignedGroupTrips = allGroupTrips?.filter(
    trip => !relationships.group_trip_ids?.includes(trip.id)
  );

  // Filter out activities that are already assigned to the attraction
  const unassignedActivities = allActivities?.filter(
    activity => !relationships.activity_ids?.includes(activity.id)
  );

  // Filter out hotels that are already assigned to the attraction
  const unassignedHotels = allHotels?.filter(
    hotel => !relationships.hotel_ids?.includes(hotel.id)
  );

  // Find the assigned package and group trip details
  const assignedPackages = allPackages?.filter(
    pkg => relationships.package_ids?.includes(pkg.id)
  );

  const assignedGroupTrips = allGroupTrips?.filter(
    trip => relationships.group_trip_ids?.includes(trip.id)
  );

  const assignedActivities = allActivities?.filter(
    activity => relationships.activity_ids?.includes(activity.id)
  );

  const assignedHotels = allHotels?.filter(
    hotel => relationships.hotel_ids?.includes(hotel.id)
  );

  const selectorClass = "flex-grow min-w-0 truncate p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal focus:border-transparent bg-white";
  const addButtonClass = (disabled: boolean) =>
    `bg-teal hover:bg-teal-dark text-white py-2 px-4 rounded-md ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Attraction Relationships | AllBounds Admin</title>
      </Helmet>

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">
          Attraction Relationships: {attraction.name}
        </h1>
        <button
          onClick={() => navigate(`/admin/attractions/${attractionId}/edit`)}
          className="bg-teal hover:bg-teal-dark text-white py-2 px-4 rounded-md flex items-center"
        >
          Edit Attraction
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Package Relationships */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Associated Packages</h2>
          <div className="mb-6">
            <div className="flex space-x-2">
              <select
                value={selectedPackageId}
                onChange={(e) => setSelectedPackageId(Number(e.target.value) || '')}
                className={selectorClass}
              >
                <option value="">Select a package to add...</option>
                {unassignedPackages?.map(pkg => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignPackage}
                disabled={!selectedPackageId || isAssigningPackage}
                className={addButtonClass(!selectedPackageId || isAssigningPackage)}
              >
                {isAssigningPackage ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
          {assignedPackages?.length === 0 ? (
            <p className="text-gray-500 italic">No packages assigned to this attraction.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {assignedPackages?.map(pkg => (
                <li key={pkg.id} className="py-3 flex justify-between items-center">
                  <span>{pkg.name}</span>
                  <button
                    onClick={() => handleRemovePackage(pkg.id)}
                    disabled={isRemovingPackage}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Group Trip Relationships */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Associated Group Trips</h2>
          <div className="mb-6">
            <div className="flex space-x-2">
              <select
                value={selectedGroupTripId}
                onChange={(e) => setSelectedGroupTripId(Number(e.target.value) || '')}
                className={selectorClass}
              >
                <option value="">Select a group trip to add...</option>
                {unassignedGroupTrips?.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    {trip.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignGroupTrip}
                disabled={!selectedGroupTripId || isAssigningGroupTrip}
                className={addButtonClass(!selectedGroupTripId || isAssigningGroupTrip)}
              >
                {isAssigningGroupTrip ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
          {assignedGroupTrips?.length === 0 ? (
            <p className="text-gray-500 italic">No group trips assigned to this attraction.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {assignedGroupTrips?.map(trip => (
                <li key={trip.id} className="py-3 flex justify-between items-center">
                  <span>{trip.name}</span>
                  <button
                    onClick={() => handleRemoveGroupTrip(trip.id)}
                    disabled={isRemovingGroupTrip}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Activity Relationships */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Associated Activities</h2>
          <div className="mb-6">
            <div className="flex space-x-2">
              <select
                value={selectedActivityId}
                onChange={(e) => setSelectedActivityId(Number(e.target.value) || '')}
                className={selectorClass}
              >
                <option value="">Select an activity to add...</option>
                {unassignedActivities?.map(activity => (
                  <option key={activity.id} value={activity.id}>
                    {activity.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignActivity}
                disabled={!selectedActivityId || isAssigningActivity}
                className={addButtonClass(!selectedActivityId || isAssigningActivity)}
              >
                {isAssigningActivity ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
          {assignedActivities?.length === 0 ? (
            <p className="text-gray-500 italic">No activities assigned to this attraction.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {assignedActivities?.map(activity => (
                <li key={activity.id} className="py-3 flex justify-between items-center">
                  <span>{activity.name}</span>
                  <button
                    onClick={() => handleRemoveActivity(activity.id)}
                    disabled={isRemovingActivity}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Hotel Relationships */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Associated Hotels</h2>
          <div className="mb-6">
            <div className="flex space-x-2">
              <select
                value={selectedHotelId}
                onChange={(e) => setSelectedHotelId(Number(e.target.value) || '')}
                className={selectorClass}
                disabled={isLoadingHotels}
              >
                <option value="">Select a hotel to add...</option>
                {unassignedHotels?.map(hotel => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name}{hotel.city ? ` (${hotel.city})` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignHotel}
                disabled={!selectedHotelId || isAssigningHotel}
                className={addButtonClass(!selectedHotelId || isAssigningHotel)}
              >
                {isAssigningHotel ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
          {assignedHotels?.length === 0 ? (
            <p className="text-gray-500 italic">No hotels assigned to this attraction.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {assignedHotels?.map(hotel => (
                <li key={hotel.id} className="py-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{hotel.name}</span>
                    {hotel.city && <span className="text-sm text-gray-500 ml-2">({hotel.city})</span>}
                    {hotel.stars && (
                      <span className="text-xs text-yellow-500 ml-2">{'★'.repeat(hotel.stars)}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveHotel(hotel.id)}
                    disabled={isRemovingHotel}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => navigate('/admin/attractions')}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md"
        >
          Back to Attractions
        </button>
      </div>
    </div>
  );
};

export default AttractionRelationshipsPage;
